import React, { useState, useEffect, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import { Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import ReelEditor from './ReelEditor';
import { FaLongArrowAltRight, FaLongArrowAltLeft } from "react-icons/fa";

const Reels = ({ user }) => {
    const [showModal, setShowModal] = useState(false);
    const [media, setMedia] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [reels, setReels] = useState([]);
    const [myReel, setMyReel] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const timeoutRef = useRef(null);
    const fileInputRef = useRef();
    const [isEditing, setIsEditing] = useState(false);
    const containerRef = useRef(null);

    const fetchReels = async () => {
        try {
            const res = await fetch('https://api.fantasyworldtrpg.cz/echo/get_reels.php');
            const data = await res.json();
            if (data.status === 'success') {
                const validReels = (data.reels || []).filter(
                    r => r && typeof r === 'object' && r.id && r.user_id && r.url
                );
                setReels(validReels);
                const my = validReels.find(r => r.user_id === user.id);
                setMyReel(my || null);
            } else {
                Swal.fire('Chyba při načítání', data.message || 'Nepodařilo se načíst seznam reels.', 'error');
            }
        } catch (err) {
            Swal.fire('Chyba načítání', 'Nepodařilo se načíst reels. Zkontroluj připojení.', 'error');
        }
    };

    const startTimeout = useCallback(() => {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            handleNext();
        }, 10000);
    }, [currentIndex, reels]);

    useEffect(() => {
        if (isViewerOpen) {
            startTimeout();
        }
        return () => clearTimeout(timeoutRef.current);
    }, [isViewerOpen, currentIndex, startTimeout]);


    useEffect(() => {
        fetchReels();
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setMedia(file);
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            setError(null);
            if (file.type.startsWith('image/')) {
                setIsEditing(true);
            }
        }
    };

    const handleEditorSave = (editedFile) => {
        setMedia(editedFile);
        setIsEditing(false);
        const url = URL.createObjectURL(editedFile);
        setPreviewUrl(url);
    };

    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    const handleSave = async () => {
        if (!media) return;
        if (!user || !user.id) {
            Swal.fire('Chyba', 'Musíš být přihlášen, aby bylo možné nahrát reel.', 'error');
            return;
        }
        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', media);
        formData.append('user', user.id);
        try {
            const res = await fetch('https://api.fantasyworldtrpg.cz/echo/upload_reel.php', {
                method: 'POST',
                body: formData
            });
            const data = await res.json();
            if (data.status === 'success') {
                Swal.fire('Úspěch', data.message || 'Reel byl úspěšně nahrán!', 'success');
                handleClose();
                fetchReels();
            } else {
                Swal.fire('Chyba při nahrávání', data.message || 'Nastala chyba při nahrávání.', 'error');
            }
        } catch (err) {
            Swal.fire('Chyba spojení', 'Zkontroluj připojení a zkus to znovu.', 'error');
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setShowModal(false);
        setMedia(null);
        setPreviewUrl(null);
        setIsUploading(false);
        setError(null);
    };

    const handleView = (index) => {
        if (!reels[index] || !reels[index].url) {
            Swal.fire('Neplatný reel', 'Tento reel nemá správná data.', 'error');
            return;
        }
        setCurrentIndex(index);
        setIsViewerOpen(true);
    };

    const handleNext = () => {
        if (currentIndex + 1 < reels.length) {
            setCurrentIndex(currentIndex + 1);
        } else {
            setIsViewerOpen(false);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => handleNext(),
        onSwipedRight: () => handlePrev(),
        preventDefaultTouchmoveEvent: true,
        trackMouse: true
    });

    const formatTimeAgo = (dateString) => {
        const now = new Date();
        const past = new Date(dateString);
        const diffMs = now - past;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHours = Math.floor(diffMin / 60);
        const diffDays = Math.floor(diffHours / 24);
        if (diffSec < 60) return `před pár sekundami`;
        if (diffMin < 60) return `před ${diffMin} minutami`;
        if (diffHours < 24) return `před ${diffHours} hodinami`;
        return `před ${diffDays} dny`;
    };

    return (
        <>
            <div
                ref={containerRef}
                className="d-flex overflow-auto touch-scroll-x"
                style={{
                    maxWidth: '100%',
                    padding: '10px',
                    whiteSpace: 'nowrap',
                    gap: '8px',
                    scrollbarWidth: 'thin',
                    WebkitOverflowScrolling: 'touch',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    touchAction: 'pan-x'
                }}
            >
                {/* Můj Reel nebo Přidat */}
                {myReel ? (
                    <div
                        style={{
                            cursor: 'pointer',
                            width: '75px',
                            height: '133px',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#000'
                        }}
                        className="position-relative group"
                    >
                        {myReel.type === 'video' ? (
                            <video
                                src={myReel.url}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                muted
                                loop
                                playsInline
                                onClick={() => handleView(reels.findIndex(r => r.id === myReel.id))}
                            />
                        ) : (
                            <img
                                src={myReel.url}
                                alt="Můj Reel"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onClick={() => handleView(reels.findIndex(r => r.id === myReel.id))}
                            />
                        )}
                        <div
                            className="position-absolute top-0 end-0 m-2 p-1 bg-light rounded-circle d-flex justify-content-center align-items-center shadow"
                            style={{
                                width: 30,
                                height: 30,
                                opacity: 0,
                                transition: 'opacity 0.3s',
                                cursor: 'pointer'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowModal(true);
                            }}
                        >
                            <i className="bi bi-plus-circle-fill text-primary"></i>
                        </div>
                        <style jsx>{`
                            .group:hover .position-absolute {
                                opacity: 1 !important;
                            }
                        `}</style>
                    </div>
                ) : (
                    <div
                        onClick={() => setShowModal(true)}
                        style={{
                            cursor: 'pointer',
                            width: '75px',
                            minHeight: '133px',
                            border: '2px dashed #ccc',
                            borderRadius: '10px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column'
                        }}
                    >
                        <i className="bi bi-plus-circle" style={{ fontSize: '2rem', color: '#999' }}></i>
                        <span style={{ fontSize: '14px', color: '#999', textAlign: 'center' }}>Nový Reel</span>
                    </div>
                )}

                {/* Ostatní Reels */}
                {reels
                    .filter(reel => reel && reel.user_id !== user.id)
                    .map((reel, index) => (
                        <div
                            key={reel.id}
                            onClick={() => handleView(index)}
                            style={{
                                flex: '0 0 auto',
                                width: '75px',
                                height: '133px',
                                borderRadius: '10px',
                                overflow: 'hidden',
                                position: 'relative',
                                cursor: 'pointer'
                            }}
                        >
                            {reel.type === 'video' ? (
                                <video
                                    src={reel.url}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    muted
                                    loop
                                    playsInline
                                />
                            ) : (
                                <img
                                    src={reel.url}
                                    alt="Reel"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            )}
                        </div>
                    ))}
            </div>

            {/* Upload Modal */}
            <Modal show={showModal} onHide={handleClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-dark">Nový Reel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger" className="text-center">{error}</Alert>}
                    {isEditing ? (
                        <ReelEditor media={media} onSave={handleEditorSave} onCancel={handleClose} />
                    ) : !previewUrl ? (
                        <div className="text-center">
                            <p className="text-dark">Vyber fotku, gif nebo video pro svůj reel.</p>
                            <Button variant="danger" onClick={handleUploadClick}>Vybrat soubor</Button>
                            <Form.Control
                                type="file"
                                accept="image/*,video/*"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                        </div>
                    ) : (
                        <div className="text-center">
                            {media.type.startsWith('video') ? (
                                <video src={previewUrl} controls style={{ maxWidth: '100%', maxHeight: '400px' }} />
                            ) : (
                                <img src={previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '400px' }} />
                            )}
                            <div className="mt-3">
                                <Button variant="secondary" onClick={() => { setPreviewUrl(null); setMedia(null); }}>
                                    Změnit soubor
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={isUploading}>Zavřít</Button>
                    <Button variant="success" onClick={handleSave} disabled={!media || isUploading}>
                        {isUploading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" /> Nahrávám...
                            </>
                        ) : (
                            'Uložit Reel'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Fullscreen Viewer */}
            <AnimatePresence>
                {isViewerOpen && reels[currentIndex] && (
                    <motion.div
                        {...swipeHandlers}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            backgroundColor: 'rgba(0,0,0,0.95)',
                            zIndex: 2000,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: '#fff',
                            textAlign: 'center'
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: 20,
                                right: 20,
                                cursor: 'pointer',
                                fontSize: '2rem'
                            }}
                            onClick={() => setIsViewerOpen(false)}
                        >
                            &times;
                        </div>
                        <div
                            style={{
                                position: 'absolute',
                                top: 20,
                                left: 20,
                                fontSize: '1rem',
                                color: '#ddd'
                            }}
                        >
                            {reels[currentIndex].nickname} • {formatTimeAgo(reels[currentIndex].created_at)}
                        </div>
                        {reels[currentIndex].type === 'video' ? (
                            <video
                                src={reels[currentIndex].url}
                                controls
                                autoPlay
                                style={{ maxWidth: '100%', maxHeight: '80vh' }}
                            />
                        ) : (
                            <img
                                src={reels[currentIndex].url}
                                alt="Reel"
                                style={{ maxWidth: '100%', maxHeight: '80vh' }}
                            />
                        )}
                        <div style={{ marginTop: 10, fontSize: '0.9rem', color: '#aaa' }}>
                            Reel {currentIndex + 1} z {reels.length}
                        </div>
                        <div
                            style={{
                                position: 'absolute',
                                bottom: 30,
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '0 30px'
                            }}
                        >
                            <Button variant="light" onClick={handlePrev} disabled={currentIndex === 0}>
                                <FaLongArrowAltLeft />
                            </Button>
                            <Button variant="light" onClick={handleNext} disabled={currentIndex + 1 >= reels.length}>
                                <FaLongArrowAltRight />
                            </Button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Reels;
